import type {
  AnomalyDefaultSeverity,
  AnomalyMeasurementType,
  AnomalyThresholdType,
  CreateAnomalyEventRequest,
} from "../../types/anomalyEvent";
import i18n from "../../i18n";
import { parseStringListField, validateCustomActivityPeriod } from "./constants";
import {
  parseOutsideWorkingHoursFlag,
  toApiLocalTimeValue,
  validateAnomalyTimeGates,
  type CustomerWorkHoursSource,
} from "./timeGates";

export const mapAnomalyEventFormToPayload = (formJson: Record<string, string>): CreateAnomalyEventRequest => {
  const payload: CreateAnomalyEventRequest = {
    sourceSystemType: formJson.sourceSystemType,
    activityPeriod: formJson.activityPeriod,
    definition: formJson.definition || undefined,
    category: formJson.category,
    subCategory: formJson.subCategory,
    alertKeywords: parseStringListField(formJson.alertKeywords),
    thresholdType: formJson.thresholdType as AnomalyThresholdType,
    correlationFields: parseStringListField(formJson.correlationFields),
    threshold: parseInt(formJson.threshold, 10),
    outsideWorkingHours: parseOutsideWorkingHoursFlag(formJson.outsideWorkingHours),
  };

  const validFrom = toApiLocalTimeValue(formJson.validFrom);
  const validTo = toApiLocalTimeValue(formJson.validTo);
  if (validFrom && validTo) {
    payload.validFrom = validFrom;
    payload.validTo = validTo;
  }

  const defaultSeverity = formJson.defaultSeverity?.trim();
  if (defaultSeverity) {
    payload.defaultSeverity = defaultSeverity as AnomalyDefaultSeverity;
  }

  if (formJson.activityPeriod === "Custom") {
    payload.measurementType = formJson.measurementType as AnomalyMeasurementType;
    payload.measurementValue = parseInt(formJson.measurementValue, 10);
  }

  const customerId = formJson.customerId?.trim();
  if (customerId) {
    payload.customerId = customerId;
  }

  return payload;
};

export const getAnomalyEventFormValidationError = (
  formJson: Record<string, string>,
  options?: {
    requireCustomer?: boolean;
    customer?: CustomerWorkHoursSource | null;
    allowMissingWorkHours?: boolean;
  },
): string | null => {
  const customError = validateCustomActivityPeriod(formJson);
  if (customError) {
    return customError;
  }

  const timeGateError = validateAnomalyTimeGates(formJson, {
    customer: options?.customer,
    allowMissingWorkHours: options?.allowMissingWorkHours,
  });
  if (timeGateError) {
    return timeGateError;
  }

  if (!formJson.sourceSystemType || !formJson.category || !formJson.subCategory) {
    return i18n.t("anomalies.validation.coreRequired");
  }

  if (!formJson.thresholdType || !formJson.activityPeriod || !formJson.threshold) {
    return i18n.t("anomalies.validation.thresholdRequired");
  }

  if (options?.requireCustomer && !formJson.customerId?.trim()) {
    return i18n.t("anomalies.validation.customerRequired");
  }

  return null;
};
